import React, { useEffect, useRef, useState, useMemo } from "react";
import TitleScreen from "./components/TitleScreen";
import EndScreen from "./components/EndScreen";
import GameScreen from "./components/GameScreen";
// prettier-ignore
import { INITIAL_HP, INITIAL_MP, INITIAL_COIN, STARTING_LEVEL, ENDING_LEVEL } from "./data/settings";
import { initializeGame } from "./game";
import { ScreenKey } from "./types";

const hints = [0, 1, 2, 3] as const;
const [NO_HINT, FORWARD_HINT, REWIND_HINT, COIN_HINT] = hints;
type HINT = typeof hints[number];

export default function App() {
  const gameRef = useRef<Phaser.Game>();

  const [level, setLevel] = useState(STARTING_LEVEL);
  const [hp, setHP] = useState(INITIAL_HP);
  const [mp, setMP] = useState(INITIAL_MP);
  const [coin, setCoin] = useState(INITIAL_COIN);
  const [screen, setScreen] = useState<ScreenKey>("Title");

  const [step, setStep] = useState<HINT>(NO_HINT);
  const [drops, setDrops] = useState<Record<string, number>>({});

  // initialize game
  useEffect(() => {
    const setters = { setHP, setMP, setCoin, setDrops, setLevel, setScreen };
    gameRef.current = initializeGame(setters);
  }, []);

  const actions = useMemo(() => {
    if (level === 0) setStep(FORWARD_HINT);
    if (level === 4) setStep(COIN_HINT);
    const closeModal = () => {
      setStep(NO_HINT);
    };
    const restartLevel = () => {
      gameRef.current?.scene.stop("Game");
      gameRef.current?.scene.start("Game", { level });
      setHP(INITIAL_HP);
      setMP(INITIAL_MP);
      setCoin(INITIAL_COIN);
    };
    return { closeModal, restartLevel };
  }, [level]);

  // once HP reaches zero, go to EndScene
  useEffect(() => {
    const reviveKey = "crystal_ball";
    const won = level >= ENDING_LEVEL;
    if (hp <= 0 || won) {
      //  you have a drop that lets you revive
      // so reduce count of drop by 1 and restart the level
      if (hp <= 0 && drops[reviveKey]) {
        setDrops((oldDrops) => ({ ...oldDrops, [reviveKey]: oldDrops[reviveKey] - 1 }));
        actions.restartLevel();
        return;
      }
      setScreen("End");
      gameRef.current?.scene.stop("Game");
      gameRef.current?.scene.start("End", { won });
    }
  }, [hp, level, drops, actions]);

  // once MP reaches zero, open Modal with 2nd tutorial
  useEffect(() => {
    if (mp <= 0) {
      setStep(REWIND_HINT);
    }
  }, [mp]);

  switch (screen) {
    case "Title":
      return <TitleScreen />;
    case "Game": {
      const props = { level, drops, mp, step };
      return <GameScreen {...props} {...actions} />;
    }
    case "End":
      return <EndScreen won={level >= ENDING_LEVEL} drops={drops} coin={coin} />;
    default:
      return null;
  }
}
